import {
  WSButton,
  WSButtonProps,
  WSButtons,
  WSModal,
  WSRadioInput,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayoutDestinationType } from "@wingspanhq/payments/dist/interfaces";
import {
  AccountStatus,
  AccountType,
  IAccount
} from "@wingspanhq/users/dist/lib/interfaces";
import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { InstitutionLogo } from "../../../components/InstitutionLogo/InstitutionLogo";
import { getAccountName } from "../../../components/WSAccountsListV2/utils";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useWSMutation } from "../../../query/helpers";
import { useUserId } from "../../../query/hooks/helpers";
import { usePayoutSettings } from "../../../query/payments/queries";
import { QUERY_USERS_ACCOUNTS } from "../../../query/users/keys";
import { useCreateAccount } from "../../../query/users/mutations";
import { useAccounts } from "../../../query/users/queries";
import { getPayoutAccount } from "../../../query/users/selectors";
import { WSQueries } from "../../../query/WSQuery";
import { paymentsService } from "../../../services/payments";
import { ErrorContextKey } from "../../../services/platform";
import { useMemberPlaidLink } from "../../../utils/usePlaidLink";
import styles from "./ChooseAccountForPayoutsModal.module.scss";

export const CHOOSE_ACCOUNT_FOR_PAYOUTS_MODAL = "chooseAccountForPayouts";

type Props = {
  onSuccess: () => void;
};

const getFilteredAccounts = (accounts: IAccount[]) => {
  return accounts.filter(
    account =>
      account.type === AccountType.Depository && account.canBeUsedFor?.payouts
  );
};

export const ChooseAccountForPayoutsModal: React.FC<Props> = ({
  onSuccess
}) => {
  const [value, setValue] = useState("");
  const userId = useUserId();
  const [createAccount, createAccountMeta] = useCreateAccount();
  const accountsQuery = useAccounts();
  const payoutSettingsQuery = usePayoutSettings(userId);

  useEffect(() => {
    if (accountsQuery.data) {
      const filteredAccounts = getFilteredAccounts(accountsQuery.data);
      const selectedAccount = getPayoutAccount(
        payoutSettingsQuery.data,
        filteredAccounts
      );

      setValue(
        selectedAccount
          ? selectedAccount.accountId
          : filteredAccounts.length > 0
          ? filteredAccounts[0].accountId
          : ""
      );
    }
  }, [
    accountsQuery.data?.length,
    accountsQuery.status,
    payoutSettingsQuery.status
  ]);

  const [submit, submitMeta] = useWSMutation<
    any,
    AxiosError,
    { accountId?: string }
  >(
    async ({ accountId }) => {
      if (accountId) {
        await paymentsService.payoutSettings.update(userId, {
          standard: {
            destinationType: PayoutDestinationType.Account,
            destinationId: accountId
          }
        });
      } else {
        throw new Error("Please, choose one account");
      }
    },
    {
      dependencies: [QUERY_USERS_ACCOUNTS],
      onSuccess
    }
  );

  const filteredAccounts = getFilteredAccounts(accountsQuery.data || []);

  const accountsByInstitution: { [key: string]: IAccount[] } = {};

  filteredAccounts.forEach(account => {
    const institution = account.institution || "";
    if (!accountsByInstitution[institution])
      accountsByInstitution[institution] = [];

    accountsByInstitution[institution].push(account);
  });

  const onSelectAccount = async () => {
    submit({ accountId: value });
  };

  return (
    <WSModal
      name={CHOOSE_ACCOUNT_FOR_PAYOUTS_MODAL}
      size="S"
      title="Where should we pay you?"
    >
      <WSQueries queries={{ accountsQuery }}>
        {() =>
          Object.keys(accountsByInstitution).length > 0 ? (
            <>
              {Object.keys(accountsByInstitution).map(institution => (
                <div key={institution} className={styles.section}>
                  <h3 className={styles.sectionTitle}>{institution}</h3>
                  <ul>
                    {accountsByInstitution[institution].map(account => (
                      <li
                        key={account.accountId}
                        onClick={() => {
                          setValue(account.accountId);
                        }}
                        data-testid={`account-item-${account.accountId}`}
                      >
                        <div className={styles.name}>
                          <InstitutionLogo
                            institutionId={account.institutionId}
                          />
                          {getAccountName(account)}
                        </div>
                        <WSRadioInput
                          name="account"
                          value={account.accountId}
                          checked={value === account.accountId}
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}

              <WSErrorMessage
                my="M"
                contextKey={ErrorContextKey.CreateAccount}
                error={createAccountMeta.error || submitMeta.error}
              />

              <WSButtons forceFullWidth format="modal">
                <WSButton
                  name="proceedSelectedAccount"
                  onClick={onSelectAccount}
                  loading={submitMeta.isLoading}
                >
                  Proceed with selected account
                </WSButton>
                <LinkAnotherAccountButton
                  kind="Secondary"
                  onSuccess={async (publicToken: string) => {
                    const accounts = (await createAccount({
                      publicToken,
                      status: AccountStatus.Pending
                    })) as IAccount[];

                    if (accounts) {
                      return {
                        success: true,
                        data: accounts
                      };
                    } else {
                      return {
                        success: false
                      };
                    }
                  }}
                >
                  Link another account
                </LinkAnotherAccountButton>
              </WSButtons>
            </>
          ) : (
            <>
              <WSText mb="M">
                We weren't able to find any accounts eligible for deposits.
                Please try connecting another bank account.{" "}
              </WSText>
              <LinkAnotherAccountButton
                fullWidth
                onSuccess={async (publicToken: string) => {
                  const accounts = (await createAccount({
                    publicToken,
                    status: AccountStatus.Pending
                  })) as IAccount[];

                  if (accounts) {
                    return {
                      success: true,
                      data: accounts
                    };
                  } else {
                    return {
                      success: false
                    };
                  }
                }}
              >
                Link a different account
              </LinkAnotherAccountButton>
            </>
          )
        }
      </WSQueries>
    </WSModal>
  );
};

type LinkAnotherAccountButtonProps = {
  onSuccess: (publicToken: string) => void;
} & WSButtonProps;

const LinkAnotherAccountButton: React.FC<LinkAnotherAccountButtonProps> = ({
  onSuccess,
  ...buttonProps
}) => {
  const plaidHandler = useMemberPlaidLink({
    onSuccess
  });
  return (
    <WSButton
      data-testid="linkAccount"
      onClick={() => {
        plaidHandler.open();
      }}
      {...buttonProps}
    />
  );
};
